import './support-badge.scss';

export const SUPPORT_HUES = 6;

/** 1-based hue slot for a support number. S1 → 1 … S6 → 6, S7 → 1 again. Non-positive → 1. */
export const supportHue = (supportNumber: number): number => {
	const n = Math.trunc(Number(supportNumber));
	if (!Number.isFinite(n) || n < 1) return 1;
	return ((n - 1) % SUPPORT_HUES) + 1;
};

export interface SupportBadgeProps {
	supportNumber: number;
	/** The member's full name. Always shown beside the badge in the `full` variant. */
	name?: string | null;
	/**
	 * `compact` — just the "S3" pill (tables, timelines), with the full label as its accessible name.
	 * `full` — "S3" pill + "Support 3 – David Musa".
	 */
	variant?: 'compact' | 'full';
	className?: string;
}

/**
 * The team member's identity mark. One component, used everywhere a member appears, so the hue for
 * S3 is the same in the roster, the panel and (later) the Situation Room.
 *
 * The pill has a doubled class: `[data-theme='dark'] .body-container span { color: inherit }`
 * otherwise beats a single-class colour in dark mode (Web/CLAUDE.md).
 */
const SupportBadge = ({ supportNumber, name, variant = 'compact', className = '' }: SupportBadgeProps) => {
	const hue = supportHue(supportNumber);
	const short = `S${supportNumber}`;
	const cleanName = name?.trim();
	const label = cleanName ? `Support ${supportNumber} – ${cleanName}` : `Support ${supportNumber}`;

	if (variant === 'compact') {
		return (
			<span
				className={`support-badge support-badge--compact ${className}`.trim()}
				title={label}
			>
				<span
					className={`support-badge__pill support-badge__pill--${hue}`}
					aria-hidden='true'
				>
					{short}
				</span>
				<span className='visually-hidden'>{label}</span>
			</span>
		);
	}

	return (
		<span className={`support-badge support-badge--full ${className}`.trim()}>
			<span
				className={`support-badge__pill support-badge__pill--${hue}`}
				aria-hidden='true'
			>
				{short}
			</span>
			<span className='support-badge__label'>{label}</span>
		</span>
	);
};

export default SupportBadge;
